import { useMemo, useState } from "react";

import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Tag } from "@/shared/components/ui/tag";
import { Mail, Phone, Plus } from "lucide-react";
import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import { Database } from "@/shared/types/database.types";
import { ContactSourceDocument, PersonContact } from "@/shared/types/estateContact";
import { formatPhoneNumber, formatLegalRole } from "@/shared/utils/formatters";
import { DocumentBadge } from "@/shared/components/DocumentBadge";
import { useDocumentSheet } from "@/shared/hooks/useDocumentSheet";

import { LawyerAddContactSheet } from "../components/LawyerAddContactSheet";
import { LawyerEditContactSheet } from "../components/LawyerEditContactSheet";

type PersonContactRow = Database["public"]["Tables"]["estate_contacts"]["Row"] & {
  source_document?: ContactSourceDocument | null;
};
type EstateInvitationRow = Database["public"]["Tables"]["estate_invitations"]["Row"];

interface JoinedUser {
  user_id: number;
  email: string | null;
}

interface LawyerPeopleTabProps {
  contacts: PersonContactRow[] | undefined;
  invitations?: EstateInvitationRow[];
  joinedUsers?: JoinedUser[];
  ownerEmail?: string | null;
  estateId?: number;
  decedentName?: string | null;
}

export function LawyerPeopleTab({
  contacts,
  invitations,
  joinedUsers,
  ownerEmail,
  estateId,
  decedentName,
}: LawyerPeopleTabProps): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<PersonContact | null>(null);
  const { openDocument, documentSheet } = useDocumentSheet(estateId);

  const deceasedUserData = decedentName
    ? { displayName: decedentName, firstName: decedentName.split(" ")[0] }
    : null;

  const visibleContacts = useMemo(() => (contacts || []).filter((c) => !c.is_hidden), [contacts]);

  const filteredContacts = useMemo(() => {
    const queryLower = searchQuery.trim().toLowerCase();
    if (!queryLower) return visibleContacts;
    return visibleContacts.filter((contact) => {
      const haystack = [
        contact.first_name,
        contact.last_name,
        contact.email,
        contact.phone,
        contact.relationship,
        ...(contact.legal_role || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(queryLower);
    });
  }, [visibleContacts, searchQuery]);

  const fullName = (c: PersonContactRow) => `${c.first_name || ""} ${c.last_name || ""}`.trim();

  const ownerEmailLower = ownerEmail?.toLowerCase();

  const sortedContacts = [...filteredContacts].sort((a, b) => {
    const aIsPrimary = !!ownerEmailLower && a.email?.toLowerCase() === ownerEmailLower;
    const bIsPrimary = !!ownerEmailLower && b.email?.toLowerCase() === ownerEmailLower;
    if (aIsPrimary && !bIsPrimary) return -1;
    if (!aIsPrimary && bIsPrimary) return 1;
    return fullName(a).toLowerCase().localeCompare(fullName(b).toLowerCase());
  });

  const joinedEmails = new Set(
    (joinedUsers || []).map((u) => u.email?.toLowerCase()).filter(Boolean) as string[],
  );
  const invitedEmails = new Set((invitations || []).map((i) => i.email.toLowerCase()));

  const getInviteStatus = (contact: PersonContactRow): "joined" | "invited" | null => {
    const email = contact.email?.toLowerCase();
    if (!email) return null;
    if (joinedEmails.has(email)) return "joined";
    if (invitedEmails.has(email)) return "invited";
    return null;
  };

  const isMe = (contact: PersonContactRow) =>
    !!ownerEmailLower && contact.email?.toLowerCase() === ownerEmailLower;

  const header = (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-stone-200">
      <div className="flex-1">
        <Input
          variant="search"
          placeholder="Search contacts"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search contacts"
        />
      </div>
      {estateId && (
        <Button variant="brand" onClick={() => setIsAddOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Add contact
        </Button>
      )}
    </div>
  );

  if (visibleContacts.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="p-0">
            {header}
            <div className="py-12 text-center">
              <p className="text-secondary">No people have been added to this estate yet.</p>
            </div>
          </CardContent>
        </Card>
        {estateId && (
          <LawyerAddContactSheet
            isOpen={isAddOpen}
            onOpenChange={setIsAddOpen}
            estateId={estateId}
            deceasedUserData={deceasedUserData}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          {header}

          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="w-[20%] text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                    Name
                  </th>
                  <th className="w-[16%] text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                    Legal Role
                  </th>
                  <th className="w-[22%] text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                    Contact info
                  </th>
                  <th className="w-[22%] text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                    Address
                  </th>
                  <th className="w-[20%] text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedContacts.map((contact) => {
                  const name = fullName(contact) || "Unknown";
                  const isPrimaryContact = isMe(contact);
                  const relationshipText = contact.relationship
                    ? getRelationshipLabel(contact.relationship)
                    : "—";
                  const legalRoleText = formatLegalRole(contact.legal_role ?? null);
                  const phoneValue = contact.phone;
                  const emailValue = contact.email;
                  const inviteStatus = getInviteStatus(contact);

                  return (
                    <tr
                      key={contact.id}
                      className={`border-b border-stone-100 last:border-b-0 hover:bg-stone-50 ${
                        estateId ? "cursor-pointer" : ""
                      }`}
                      onClick={
                        estateId ? () => setEditingContact(contact as PersonContact) : undefined
                      }
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-primary">{name}</span>
                          {isPrimaryContact && (
                            <Tag
                              color="green"
                              className="bg-brand-subtle text-brand border border-brand px-3 py-0.5 text-2xs font-normal"
                            >
                              Primary
                            </Tag>
                          )}
                          {inviteStatus === "joined" && (
                            <Tag
                              color="green"
                              className="bg-green-50 text-green-700 border border-green-200 px-2.5 py-0.5 text-2xs font-normal"
                            >
                              Joined
                            </Tag>
                          )}
                          {inviteStatus === "invited" && (
                            <Tag
                              color="yellow"
                              className="bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-2xs font-normal"
                            >
                              Invited
                            </Tag>
                          )}
                        </div>
                        {contact.relationship && (
                          <div className="text-sm text-secondary mt-0.5">{relationshipText}</div>
                        )}
                      </td>
                      <td className="py-4 px-6 text-sm text-secondary">{legalRoleText}</td>
                      <td className="py-4 px-6 text-sm">
                        {emailValue || phoneValue ? (
                          <div className="flex flex-col gap-1">
                            {emailValue && (
                              <a
                                href={`mailto:${emailValue}`}
                                className="flex items-center gap-2 text-brand"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Mail className="h-4 w-4" />
                                {emailValue}
                              </a>
                            )}
                            {phoneValue && (
                              <a
                                href={`tel:${phoneValue}`}
                                className="flex items-center gap-2 text-brand whitespace-nowrap"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Phone className="h-4 w-4" />
                                {formatPhoneNumber(phoneValue)}
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-tertiary">—</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-sm">
                        {contact.address ? (
                          <span>{contact.address}</span>
                        ) : (
                          <span className="text-tertiary">—</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-sm" onClick={(e) => e.stopPropagation()}>
                        {contact.extracted_from_document && contact.source_document ? (
                          <DocumentBadge
                            sourceDocument={contact.source_document}
                            variant="long"
                            onClick={() => openDocument(contact.source_document)}
                            color="stone"
                            shimmer={false}
                            className="max-w-[150px] text-2xs"
                            maxNameWidth="160px"
                          />
                        ) : (
                          <span className="text-tertiary">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {estateId && (
        <>
          <LawyerAddContactSheet
            isOpen={isAddOpen}
            onOpenChange={setIsAddOpen}
            estateId={estateId}
            deceasedUserData={deceasedUserData}
          />
          <LawyerEditContactSheet
            isOpen={!!editingContact}
            onOpenChange={(open) => !open && setEditingContact(null)}
            contact={editingContact}
            estateId={estateId}
            deceasedUserData={deceasedUserData}
            isPrimary={
              !!ownerEmailLower && editingContact?.email?.toLowerCase() === ownerEmailLower
            }
          />
        </>
      )}
      {documentSheet}
    </>
  );
}
